import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import ImageUploader from '../../components/ImageUploader';

export const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, service: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, service: null });
  const [formData, setFormData] = useState({
    category: '',
    icon: 'Scissors',
    description: '',
    image: '',
    items: [],
    isActive: true
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/admin/services');
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      category: '',
      icon: 'Scissors',
      description: '',
      image: '',
      items: [{ name: '', price: '', duration: '' }],
      isActive: true
    });
    setEditDialog({ open: true, service: null });
  };

  const openEditDialog = (service) => {
    setFormData({
      category: service.category,
      icon: service.icon,
      description: service.description,
      image: service.image,
      items: service.items || [],
      isActive: service.isActive !== false
    });
    setEditDialog({ open: true, service });
  };

  const handleSave = async () => {
    try {
      if (editDialog.service) {
        await api.patch(`/admin/services/${editDialog.service.id}`, formData);
        toast.success('Service mis à jour');
      } else {
        await api.post('/admin/services', formData);
        toast.success('Service créé');
      }
      setEditDialog({ open: false, service: null });
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (serviceId) => {
    try {
      await api.delete(`/admin/services/${serviceId}`);
      toast.success('Service supprimé');
      setDeleteDialog({ open: false, service: null });
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { name: '', price: '', duration: '' }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion des Services</h1>
            <p className="text-sm md:text-base text-[#5D4037] mt-1">Gérez vos catégories de services et tarifs</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
            <Plus className="mr-2" size={20} />
            Ajouter un service
          </Button>
        </div>
      </div>

      {/* Services List - Mobile Optimized */}
      <div className="space-y-4">
        {services.map((service) => (
          <Card key={service.id} className={!service.isActive ? 'opacity-60' : ''}>
            <CardContent className="p-0">
              <Accordion type="single" collapsible>
                <AccordionItem value={service.id} className="border-none">
                  <AccordionTrigger className="px-4 md:px-6 py-4 hover:no-underline">
                    <div className="flex items-center gap-3 md:gap-4 w-full">
                      <img
                        src={service.image}
                        alt={service.category}
                        className="w-12 h-12 md:w-16 md:h-16 object-cover rounded-lg shrink-0"
                      />
                      <div className="flex-1 text-left min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-[#2C1810] truncate">{service.category}</h3>
                        <p className="text-xs md:text-sm text-[#5D4037]">{service.items?.length || 0} prestations</p>
                      </div>
                      <div className="hidden sm:flex items-center gap-2 mr-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); openEditDialog(service); }}
                          className="text-[#5D4037] hover:text-[#2C1810]"
                        >
                          <Edit2 size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, service }); }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-4 md:px-6 pb-4">
                      <p className="text-sm text-[#5D4037] mb-4">{service.description}</p>
                      <div className="space-y-2">
                        {service.items?.map((item, index) => (
                          <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4 p-3 bg-[#F5F0EB] rounded-lg">
                            <span className="font-medium text-[#2C1810] text-sm md:text-base">{item.name}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-xs md:text-sm text-[#5D4037]">{item.duration}</span>
                              <span className="font-semibold text-[#C9A96E] text-sm md:text-base">{item.price}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Mobile action buttons */}
                      <div className="flex gap-2 mt-4 sm:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(service)}
                          className="flex-1"
                        >
                          <Edit2 size={14} className="mr-1" /> Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, service })}
                          className="text-red-600 border-red-300"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        ))}
      </div>

      {services.length === 0 && (
        <div className="text-center py-12">
          <p className="text-[#5D4037]">Aucun service trouvé</p>
        </div>
      )}

      {/* Edit/Create Dialog - Mobile Optimized */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="w-[95vw] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editDialog.service ? 'Modifier le service' : 'Nouveau service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la catégorie</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Coiffure Afro"
                />
              </div>
              <div className="space-y-2">
                <Label>Icône</Label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Ex: Scissors, Heart, Sparkles"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du service..."
                rows={3}
              />
            </div>
            
            <ImageUploader
              category="services"
              currentImage={formData.image}
              onImageUploaded={(url) => setFormData({ ...formData, image: url })}
              label="Image du service"
            />

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label>Service actif</Label>
            </div>

            {/* Items - Mobile Optimized */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                <Label className="text-lg">Prestations</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="w-full sm:w-auto">
                  <Plus size={16} className="mr-1" /> Ajouter
                </Button>
              </div>
              
              {formData.items.map((item, index) => (
                <div key={index} className="flex flex-col gap-3 p-4 bg-[#F5F0EB] rounded-lg">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-[#5D4037]">Prestation {index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-700 -mt-1 -mr-1"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  <Input
                    value={item.name}
                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                    placeholder="Nom de la prestation"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={item.price}
                      onChange={(e) => updateItem(index, 'price', e.target.value)}
                      placeholder="Prix (ex: 80€)"
                    />
                    <Input
                      value={item.duration}
                      onChange={(e) => updateItem(index, 'duration', e.target.value)}
                      placeholder="Durée (ex: 2h)"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, service: null })} className="w-full sm:w-auto">
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
                {editDialog.service ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - Mobile Optimized */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce service ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le service "{deleteDialog.service?.category}" et toutes ses prestations seront supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.service?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
